import React, { useEffect, useState } from 'react';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import { Grid, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './assets/fonts/fonts.css';
import './App.css';
import './components/loading/index.css';

import routeData from './../src/config/routes.json';
import PageNotFound from './pages/PageNotFound';

import loadingIndicator from './assets/animation/loadingIndicator.json';
import { Player } from '@lottiefiles/react-lottie-player';

import { getKeycloakLoginUrl, loadUserKeycloakInfo } from './services';
import { ToasterProvider, UserProvider } from './context';

import Header from './components/header';
import SessionExpiryModal from 'components/modal/sessionExpiry';

/* ------------------ TanStack Query Client ------------------ */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  }
});

/* ------------------ Lazy Page Mapping ------------------ */

type PageComponentType = React.ComponentType;

const pageMap: Record<string, () => Promise<{ default: PageComponentType }>> = {
  dashboard: () => import('./pages/Dashboard'),
  doctorManagements: () => import('./pages/DoctorManagement'),
  clinicManagement: () => import('./pages/ClinicManagement'),
  patientManagement: () => import('./pages/PatientManagement'),
  demographics: () => import('./pages/Demographics'),
  appPerformance: () => import('./pages/AppPerformance'),
  specialisationManager: () => import('./pages/SpecialisationManager'),
  chat: () => import('./pages/NewChat'),
  doctorsVerification: () => import('./pages/DoctorsVerification'),
  doctorView: () => import('./pages/DoctorsVerification/View'),
  viewDoctor: () => import('./pages/DoctorsVerification'),
  organisationManagement: () => import('./pages/OrganisationManagement'),
  roleManagement: () => import('./pages/RoleManagement'),
  doctorDetails: () => import('./pages/DoctorDetails'),
  doctorAppointmentDetail: () => import('./pages/DoctorManagement/Appointments'),
  organisationManagementDetails: () => import('./pages/OrganisationManagement/Details'),
  patientManagementDetails: () => import('./pages/PatientManagement/Details'),
  organisationManagementDoctor: () => import('./pages/OrganisationManagement/Doctor'),
  organisationManagementAllDetails: () => import('./pages/OrganisationManagement/ViewMore'),
  healthPlan: () => import('./pages/HealthPlan'),
  superAdminProfile: () => import('./pages/UserProfile'),
  practiseAdminProfile: () => import('./pages/UserProfile/UpdateOrganisation'),
  healthDiabetesManagementPlan: () => import('./pages/HealthDiabetesManagementPlan'),
  healthAddGoals: () => import('./pages/HealthAddGoals'),
  healthCreateTask: () => import('./pages/HealthCreateTask'),
  healthSurveyTree: () => import('./pages/HealthSurvey'),
  healthConitorvitalsDaily: () => import('./pages/HealthMonitorVitalsDaily'),
  surveyTreeEdit: () => import('./pages/HealthSurveyEdit'),
  departmentManagement: () => import('./pages/DepartmentManagement'),
  breathTraining: () => import('./pages/BreathTraining/index'),
  report: () => import('./pages/BreathTraining/Report')
};

/* ------------------ App Component ------------------ */

type AppProps = Readonly<{
  token: string | null;
}>;

// Create a memoized lazy map outside your component
const pageMapLazy: Record<string, React.LazyExoticComponent<PageComponentType>> = {};
Object.keys(pageMap).forEach((key) => {
  pageMapLazy[key] = React.lazy(pageMap[key]);
});

const loadPage = (pageName: string): PageComponentType => {
  return pageMapLazy[pageName] || PageNotFound;
};

function App({ token }: AppProps) {
  const [authToken, setAuthToken] = useState<string | null>(token);
  const [sessionExpired, setSessionExpired] = useState(false);

  const user_access_token = localStorage.getItem('user_access_token') || '';

  /* -------- Session Expiry Listener -------- */

  useEffect(() => {
    const handleSessionExpired = () => setSessionExpired(true);

    globalThis.addEventListener('SESSION_EXPIRED', handleSessionExpired);

    return () => {
      globalThis.removeEventListener('SESSION_EXPIRED', handleSessionExpired);
    };
  }, []);

  /* -------- Login Handler -------- */

  const handleLogin = () => {
    const loginUrl = getKeycloakLoginUrl();
    window.location.href = loginUrl;
  };

  /* -------- Auth Check -------- */

  useEffect(() => {
    const checkAuthToken = async () => {
      const storedToken = sessionStorage.getItem('authToken');
      if (!storedToken || storedToken === 'null') {
        window.location.href = getKeycloakLoginUrl();
        return;
      }

      try {
        await loadUserKeycloakInfo(storedToken, user_access_token);
        setAuthToken(storedToken);
      } catch (err) {
        console.error('Failed to load user info', err);
        window.location.href = getKeycloakLoginUrl();
      }
    };

    checkAuthToken();
  }, [user_access_token]);

  /* -------- Loader -------- */

  if (!authToken) {
    return (
      <div className="loaderWrapper">
        <Player loop src={loadingIndicator} className="loaderSize" />
      </div>
    );
  }

  /* ------------------ Layout ------------------ */

  return (
    <Box className="app-container">
      <Box component="header" className="header">
        <Header />
      </Box>

      <Grid container className="main-content">
        <Grid item xs={12} className="content-area">
          <Grid item xs={12} className="routes">
            <React.Suspense
              fallback={
                <div className="loaderWrapper">
                  <Player loop src={loadingIndicator} className="loaderSize" />
                </div>
              }>
              <Routes>
                {routeData.map((route) => {
                  const PageComponent = loadPage(route.name);

                  if (Array.isArray(route.path)) {
                    return route.path.map((path) => (
                      <Route key={path} path={path} element={<PageComponent />} />
                    ));
                  }

                  return <Route key={route.path} path={route.path} element={<PageComponent />} />;
                })}

                <Route path="*" element={<PageNotFound />} />
              </Routes>
            </React.Suspense>
          </Grid>
        </Grid>
      </Grid>

      <SessionExpiryModal
        isOpen={sessionExpired}
        onClose={() => setSessionExpired(false)}
        onLogin={handleLogin}
      />
    </Box>
  );
}

/* ------------------ App Wrapper ------------------ */

export default function AppWrapper(props: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ToasterProvider>
        <UserProvider>
          <BrowserRouter>
            <App {...props} />
          </BrowserRouter>
        </UserProvider>
      </ToasterProvider>
    </QueryClientProvider>
  );
}
